<script setup>
// [xleaders] Nhật ký tư vấn theo Opportunity, hiện trong sidebar phải của hội thoại.
//
// Chỉ ĐỌC. Việc gắn hội thoại vào Opp vẫn nằm ở panel Dashboard App (tab "Twenty CRM") — ở đây
// không có nút bấm nào đổi dữ liệu, để sale đọc lịch sử mà không sợ lỡ tay gắn nhầm.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CrmPanelAPI from 'dashboard/api/xleaders/crmPanel';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const STAGE = {
  LEAD_C0: 'C0 Rác',
  LEAD_C1: 'C1 Tiềm năng',
  LEAD_C2: 'C2 Quan tâm',
  LEAD_C3: 'C3 Chờ CK',
  LEAD_C31: 'C31 Chờ xác nhận',
  LEAD_C4: 'C4 Đã CK',
  LEAD_C41: 'C41 Đóng HP bổ sung',
};

const CLASS_STATUS = {
  SAP_DIEN_RA: 'Sắp diễn ra',
  DANG_DIEN_RA: 'Đang diễn ra',
  DA_KET_THUC: 'Đã kết thúc',
};

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const loading = ref(true);
const error = ref('');
const state = ref(null);
const classesByOpp = ref({});

const attributedId = computed(
  () => state.value?.attribution?.opportunity_id || null
);

// Opp đang gắn lên đầu — đó là câu trả lời cho "cuộc chat này thuộc deal nào", sale không phải
// cuộn tìm. Phần còn lại giữ nguyên thứ tự server trả về (đã sắp theo lần chạm gần nhất).
const opportunities = computed(() => {
  const list = state.value?.opportunities || [];
  if (!attributedId.value) return list;
  return [
    ...list.filter(o => o.id === attributedId.value),
    ...list.filter(o => o.id !== attributedId.value),
  ];
});

// Opp đang gắn không nằm trong danh sách = bị xoá bên CRM, hoặc contact đã nối sang Person khác nên
// attribution cũ trỏ vào deal của người khác. Im lặng ở đây thì sale tưởng hội thoại chưa gắn gì.
const attributedIsListed = computed(() =>
  (state.value?.opportunities || []).some(o => o.id === attributedId.value)
);

const shortDate = iso => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('vi-VN');
};

// `last_interaction_at` là tuongTacGanNhat THẬT; nó trống ở 194/200 opp nên phải có đường thay thế,
// nhưng `updatedAt` là ngày sửa bản ghi — gọi nó là ngày tư vấn thì sale đọc ra số sai. Hai nhãn.
const metaLine = opportunity => {
  const parts = [];
  if (opportunity.consultant) parts.push(opportunity.consultant);
  if (opportunity.consultation_count) {
    parts.push(`${opportunity.consultation_count} lần tư vấn`);
  }
  if (opportunity.last_interaction_at) {
    parts.push(`chạm ${shortDate(opportunity.last_interaction_at)}`);
  } else if (opportunity.last_activity_at) {
    parts.push(`cập nhật ${shortDate(opportunity.last_activity_at)}`);
  }
  return parts.join(' · ');
};

const classLabel = klass =>
  [
    klass.code,
    CLASS_STATUS[klass.status] || klass.status,
    klass.starts_on ? shortDate(klass.starts_on) : null,
  ]
    .filter(Boolean)
    .join(' · ');

// ContactPanel KHÔNG đặt `:key` theo hội thoại nên component này sống xuyên qua việc đổi hội thoại —
// chỉ có `watch` chạy lại. Không có token phiên thì response của hội thoại A về sau hội thoại B sẽ
// ghi đè, và sale đọc nhật ký của khách KHÁC trên hội thoại đang mở. Mọi phép gán phải qua guard này.
let requestToken = 0;

// Lớp học nạp SAU và nuốt lỗi: nó tốn thêm 2 request CRM, gộp vào một lần gọi là 3 request tuần tự
// và tiến sát Rack::Timeout 15s. Hỏng thì mất badge lớp, phần nhật ký vẫn dùng được.
const fetchClasses = async token => {
  const ids = (state.value?.opportunities || []).map(o => o.id);
  if (!ids.length) return;
  try {
    const { data } = await CrmPanelAPI.classes(
      accountId.value,
      props.conversationId,
      ids
    );
    if (token !== requestToken) return;
    classesByOpp.value = data.classes || {};
  } catch (e) {
    if (token !== requestToken) return;
    classesByOpp.value = {};
  }
};

const fetchLog = async () => {
  requestToken += 1;
  const token = requestToken;
  loading.value = true;
  error.value = '';
  classesByOpp.value = {};
  try {
    const { data } = await CrmPanelAPI.consultationLog(
      accountId.value,
      props.conversationId
    );
    if (token !== requestToken) return;
    state.value = data;
    await fetchClasses(token);
  } catch (e) {
    if (token !== requestToken) return;
    // Controller trả message tiếng Việt có nghĩa cho ca CRM hỏng/chưa cấu hình — ưu tiên nó,
    // chuỗi chung chỉ để cứu ca mạng đứt giữa chừng.
    state.value = null;
    error.value =
      e.response?.data?.error || t('CONVERSATION_SIDEBAR.XLEADERS_CRM.ERROR');
  } finally {
    if (token === requestToken) loading.value = false;
  }
};

watch(() => props.conversationId, fetchLog, { immediate: true });
</script>

<template>
  <div class="px-4 py-2 text-n-slate-12">
    <div v-if="loading" class="flex items-center justify-center p-4">
      <Spinner :size="24" class="text-n-brand" />
    </div>
    <div v-else-if="error" class="text-sm text-center text-n-ruby-11">
      {{ error }}
    </div>
    <div v-else-if="!state?.linked" class="text-sm text-center text-n-slate-11">
      {{ $t('CONVERSATION_SIDEBAR.XLEADERS_CRM.NOT_LINKED') }}
    </div>
    <div
      v-else-if="!opportunities.length"
      class="text-sm text-center text-n-slate-11"
    >
      {{ $t('CONVERSATION_SIDEBAR.XLEADERS_CRM.NO_OPPORTUNITIES') }}
    </div>
    <div v-else class="flex flex-col gap-3">
      <div
        v-if="attributedId && !attributedIsListed"
        class="text-xs text-n-amber-11"
      >
        {{ $t('CONVERSATION_SIDEBAR.XLEADERS_CRM.ATTRIBUTED_MISSING') }}
      </div>
      <div
        v-for="opportunity in opportunities"
        :key="opportunity.id"
        class="flex flex-col gap-1.5 p-2 border rounded-lg"
        :class="
          opportunity.id === attributedId
            ? 'border-n-brand bg-n-brand/5'
            : 'border-n-weak'
        "
      >
        <div class="flex items-baseline justify-between gap-2">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ opportunity.code || opportunity.name || '—' }}
          </span>
          <span
            v-if="opportunity.id === attributedId"
            class="text-xs shrink-0 text-n-brand"
          >
            {{ $t('CONVERSATION_SIDEBAR.XLEADERS_CRM.ATTRIBUTED') }}
          </span>
        </div>

        <div class="flex flex-wrap items-center gap-1">
          <span
            v-if="opportunity.stage"
            class="px-1.5 py-px text-xs rounded text-n-slate-12 bg-n-alpha-2"
          >
            {{ STAGE[opportunity.stage] || opportunity.stage }}
          </span>
          <span
            v-for="klass in classesByOpp[opportunity.id] || []"
            :key="klass.id"
            class="px-1.5 py-px text-xs rounded text-n-slate-11 bg-n-alpha-1"
          >
            {{ classLabel(klass) }}
          </span>
        </div>

        <div v-if="metaLine(opportunity)" class="text-xs text-n-slate-11">
          {{ metaLine(opportunity) }}
        </div>

        <p
          v-if="opportunity.consultation_log"
          class="px-2 py-1.5 text-xs whitespace-pre-wrap break-words rounded text-n-slate-12 bg-n-alpha-1"
        >
          {{ opportunity.consultation_log }}
        </p>
        <p v-else class="text-xs italic text-n-slate-10">
          {{ $t('CONVERSATION_SIDEBAR.XLEADERS_CRM.NO_LOG') }}
        </p>
      </div>
    </div>
  </div>
</template>
